import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { toast } from "react-toastify";

interface EditBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  initialTitle: string;
  initialImage: string; // mobile image
  webImage: string; // web image
}

const EditBanner: React.FC<EditBannerModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialTitle,
  initialImage,
  webImage,
}) => {
  const [title, setTitle] = useState(initialTitle);

  const [mobileImage, setMobileImage] = useState<File | null>(null);
  const [mobilePreview, setMobilePreview] = useState(initialImage);
  const mobileFileInputRef = useRef<HTMLInputElement | null>(null);

  const [webBanner, setWebBanner] = useState<File | null>(null);
  const [webPreview, setWebPreview] = useState(webImage);
  const webFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTitle(initialTitle);
    setMobilePreview(initialImage);
    setWebPreview(webImage);
    setMobileImage(null);
    setWebBanner(null);

    if (mobileFileInputRef.current) mobileFileInputRef.current.value = "";
    if (webFileInputRef.current) webFileInputRef.current.value = "";
  }, [initialTitle, initialImage, webImage]);

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setMobileImage(file);
      setMobilePreview(URL.createObjectURL(file));
    }
  };

  const handleWebChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setWebBanner(file);
      setWebPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData();
    let hasChanges = false;

    if (title.trim() !== initialTitle.trim()) {
      formData.append("title", title);
      hasChanges = true;
    }

    if (mobileImage) {
      formData.append("image", mobileImage);
      hasChanges = true;
    }

    if (webBanner) {
      formData.append("web_image", webBanner);
      hasChanges = true;
    }

    if (!hasChanges) {
      toast.info("No changes detected");
      return;
    }

    onSubmit(formData);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { padding: 20, borderRadius: 12 } }}
    >
      <DialogTitle
        style={{
          fontFamily: "Karbon-semi",
          fontSize: 22,
          textAlign: "center",
          paddingBottom: 0,
        }}
      >
        Edit Banner
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit} style={{ marginTop: 10 }}>
          {/* Title */}
          <label
            style={{
              fontFamily: "Karbon-medium",
              display: "block",
              marginBottom: 6,
            }}
          >
            Title
          </label>
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              fontFamily: "Karbon-regular",
              width: "100%",
              padding: "8px 12px",
              fontSize: 16,
              borderRadius: 8,
              border: "1px solid #ccc",
              boxSizing: "border-box",
            }}
          />

          {/* Mobile Banner Upload */}
          <label
            style={{
              fontFamily: "Karbon-medium",
              marginTop: 15,
              display: "block",
              marginBottom: 6,
            }}
          >
            Mobile Banner
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleMobileChange}
            ref={mobileFileInputRef}
            style={{ display: "block", marginBottom: 10 }}
          />
          {mobilePreview && (
            <img
              src={mobilePreview}
              alt="Mobile Preview"
              style={{
                width: "100%",
                maxHeight: 180,
                objectFit: "contain",
                borderRadius: 8,
                marginBottom: 10,
              }}
              className="modal-image-preview"
            />
          )}

          {/* Web Banner Upload */}
          <label
            style={{
              fontFamily: "Karbon-medium",
              marginTop: 15,
              display: "block",
              marginBottom: 6,
            }}
          >
            Web Banner
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleWebChange}
            ref={webFileInputRef}
            style={{ display: "block", marginBottom: 10 }}
          />
          {webPreview && (
            <img
              src={webPreview}
              alt="Web Preview"
              style={{
                width: "100%",
                maxHeight: 180,
                objectFit: "contain",
                borderRadius: 8,
                marginBottom: 20,
              }}
              className="modal-image-preview"
            />
          )}

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 20,
            }}
          >
            <button
              type="submit"
              style={{
                fontFamily: "Karbon-medium",
                fontSize: 18,
                background: "#D9490C",
                color: "white",
                border: "none",
                borderRadius: 10,
                padding: "10px 24px",
                cursor: "pointer",
              }}
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{
                fontFamily: "Karbon-medium",
                fontSize: 18,
                marginLeft: 10,
                padding: "10px 24px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditBanner;
